/**
 * Service class in charge of mapping the state to props.
 */
import alertService from '../AlertService';
import { t } from '../i18nService';
import livestreamActions from '../../actions/LivestreamActions';
import sessionReader from '../../store/readHelpers/SessionReader';
import utilService from '../UtilService';

const { liveSessionConnectionStatus, hardwareAccessStatus, proctoringMode } = livestreamActions;

const liveSessionService = {
    handleLiveSessionConnectionAlert(previousStatus, currentStatus) {
        const fromConnectedToReconnecting = previousStatus === liveSessionConnectionStatus.CONNECTED &&
            currentStatus === liveSessionConnectionStatus.RECONNECTING;
        const fromReconnectingToConnected = previousStatus === liveSessionConnectionStatus.RECONNECTING &&
            currentStatus === liveSessionConnectionStatus.CONNECTED;

        if (fromConnectedToReconnecting) {
            alertService.showMessage({
                body: t('liveSession.fromConnectedToReconnecting'),
            });
        } else if (fromReconnectingToConnected) {
            alertService.showMessage({
                body: t('liveSession.fromReconnectingToConnected'),
            });
        }
    },

    areRequiredDevicesBeingRecorded(livestream) {
        const isRemoteProctoringWebCamEnabled = sessionReader.isRemoteProctoringWebCamEnabled();
        const isRemoteProctoringMicEnabled = sessionReader.isRemoteProctoringMicEnabled();

        /*
         * If a required stream isn't streaming, it means connections to
         * recorder are not ready, so it returns false.
         */
        if (isRemoteProctoringWebCamEnabled && !livestream.isWebcamStreamingStarted) {
            return false;
        }

        if (isRemoteProctoringMicEnabled && !livestream.isAudioStreamingStarted) {
            return false;
        }

        /*
         * When we reach this line, it means that all the required streams are
         * streaming. But we also reach this line if the test does not require a
         * webcam or microphone!
         *
         * So, logically, if no stream is required, this function should return
         * false.
         * Therefore this return line checks if a stream is required:
         * - If a stream is required, it has already been checked above and we
         *   can return true.
         * - If no stream is required, there is no notion of streaming and
         *   therefore we return false.
         */
        return isRemoteProctoringWebCamEnabled || isRemoteProctoringMicEnabled;
    },

    isHardwareAccessGranted(livestream) {
        return livestream.hardwareAccessStatus === hardwareAccessStatus.GRANTED;
    },

    isHardwareAccessRejected(livestream) {
        return livestream.hardwareAccessStatus === hardwareAccessStatus.REJECTED;
    },

    isHardwareAccessNotRequested(livestream) {
        return livestream.hardwareAccessStatus === hardwareAccessStatus.NOT_REQUESTED;
    },

    areHardwareSettingsConfirmed(livestream) {
        return !!livestream.hasConfirmedHardwareSettings;
    },

    getRegisteredCall(livestream, callId) {
        return livestream?.registeredCalls?.[callId] || null;
    },

    isCallProctoringMode(proctoringData) {
        return proctoringData?.proctoringMode === proctoringMode.CALL;
    },

    isWatchProctoringMode(proctoringData) {
        return proctoringData?.proctoringMode === proctoringMode.WATCH;
    },

    isCallStarted(currentCallState, newCallState) {
        return this.isWatchProctoringMode(currentCallState) && this.isCallProctoringMode(newCallState);
    },

    isCallFinished(currentCallState, newCallState) {
        return this.isCallProctoringMode(currentCallState) && this.isWatchProctoringMode(newCallState);
    },

    getAudioEnhancements() {
        const audioEnhancements = sessionReader.getLiveSessionAudioEnhancementsJson();
        return utilService.isObject(audioEnhancements?.[0], true) ? audioEnhancements[0] : {};
    },
};

export default liveSessionService;
